import {Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State} from '@stencil/core'
import {
  BUTTONS_SIZES_TYPES,
  BUTTONS_TYPES,
  ICON_TYPES,
  LINK_RELATIONSHIPS,
  LINK_TARGETS,
  VARIANTS_KINDS,
  VARIANTS_TYPES,
  hasShadowDom,
} from '../../utils'

@Component({
  tag: 'snap-button',
  styleUrl: 'snap-button.scss',
  shadow: true,
})
export class SnapButton {
  /**
   * Controls the button text.
   */
  @Prop() text: string
  /**
   * Controls the button size.
   */
  @Prop({ reflect: true }) size: BUTTONS_SIZES_TYPES = 'md'
  /**
   * Controls the underlying markup based on the use case for the button.
   */
  @Prop({ reflect: true }) buttonType: BUTTONS_TYPES = 'button'
  /**
   * Only valid for input types (submit, reset).
   */
  @Prop({ reflect: true }) value: string
  /**
   * If this is to be a button, The name of the button, submitted as a pair with the button’s value as part of the form data, when that button is used to submit the form.
   */
  @Prop() name: string
  /**
   * Determines link behavior. Only valid for links.
   */
  @Prop({ reflect: true }) href: string
  /**
   * Does if this is an href, does it need a special relationship label
   */
  @Prop() rel: LINK_RELATIONSHIPS
  /**
   * Determines how links open. Only valid for links.
   */
  @Prop({ reflect: true }) target: LINK_TARGETS
  /**
   * Label for the link. Aria labels describe the purpose of a link in HTML
   */
  @Prop() label: string
  /**
   * Controls whether this button is disabled.
   */
  @Prop({ reflect: true }) disabled: boolean
  /**
   * Controls whether this button has an icon
   */
  @Prop({ reflect: true }) icon: ICON_TYPES
  /**
   * Controls if icon is on the left or right
   */
  @Prop({ reflect: true }) iconPosition: 'left' | 'right'
  /**
   * Controls shape of button
   */

  /**
   * Determines the primary appearance of the button based on its purpose.
   */
  @Prop({ reflect: true }) variant: VARIANTS_TYPES = 'tertiary'

  @Prop() isStacked: boolean = false

  @Prop() selector: boolean = false

  @Prop({ reflect: true, mutable: true }) checked: boolean = false

  @Prop() fullWidth: boolean = false

  @Prop() onSubmitBtn: boolean = false

  @Prop() inputBtn: boolean = false

  @Prop() iconOnlyMobile: boolean = false

  @State() iconOnly = false

  @State() isMobile: boolean

  @Element() btnTextEl: HTMLElement

  private hostEl: HTMLElement

  private hiddenButton: HTMLElement

  private mobileWidth = 768

  @Event({ eventName: 'snap-button-selected', bubbles: true, composed: true }) snapButtonSelected: EventEmitter

  @Listen('resize', { target: 'window' })
  checkScreen() {
    this.isMobile = document.body.clientWidth < this.mobileWidth

    if (this.iconOnlyMobile && this.isMobile) {
      this.iconOnly = true
    }
    else if (!this.isMobile && this.btnTextEl.textContent.trim()) {
      this.iconOnly = false
    }

  }

  componentWillLoad() {
    this.checkScreen()
  }

  componentDidLoad() {
    if (!this.btnTextEl.textContent.trim()) {
      this.iconOnly = true
    }

    if (this.onSubmitBtn) {
      this.hiddenButton = document.createElement('button')
      this.hiddenButton.hidden = true
      this.hostEl.parentNode.insertBefore(this.hiddenButton, this.hostEl.nextSibling)
    }
  }

  disconnectedCallback() {
    this.hiddenButton?.remove()
  }

  getClasses() {
    const classes = [`${this.size}`, 'snap-button--circular']
    this.isStacked ? classes.push('snap-button-group-item') : classes.push('snap-button')

    if (this.iconOnly) {
      classes.push(`icon-only`)
    }

    if (this.iconPosition === 'right') {
      classes.push(`right`)
    }

    if (this.inputBtn) {
      classes.push(`snap-button--rectangular`)
    }

    if (this.variant in VARIANTS_KINDS) {
      classes.push(`snap-button-${this.variant}`)
    }

    if (this.checked) {
      classes.push(`snap-button--checked`)
    }

    if (this.fullWidth) {
      classes.push('full-width')
    }

    return classes.join(' ').toString()
  }

  getIconSize() {
    if (this.variant === 'grouped') {
      return 'xs'
    }
    switch (this.size) {
      case 'xs':
      case 'sm':
      case 'md':
        return 'sm'
      case 'lg':
      case 'xl':
        return 'md'
      case 'xxl':
        return !this.iconOnly ? 'md' : 'lg'
    }
  }

  onClickHandler(event: Event) {
    if (this.selector) {
      if (!this.checked) {
        this.checked = true
        this.snapButtonSelected.emit(this.btnTextEl)
        return
      }
    }

    if (hasShadowDom(this.btnTextEl)) {
      const form = this.btnTextEl.closest('form')

      if (form && this.buttonType !== 'button') {
        event.preventDefault()

        const fakeButton = document.createElement('button')
        fakeButton.type = this.buttonType
        fakeButton.style.display = 'none'
        form.appendChild(fakeButton)
        fakeButton.click()
        fakeButton.remove()

        return
      }
    }
  }

  render() {
    const ButtonTypeTag = this.href ? 'a' : 'button'
    return (
      <Host
        ref={el => (this.hostEl = el)}
        class={{
          [`snap-button-${this.variant}`]: true,
          'snap-button-disabled': this.disabled
        }}>
        <ButtonTypeTag
          type={this.buttonType}
          value={this.buttonType === 'submit' ? this.value : null}
          href={this.href ? this.href : null}
          target={this.href ? this.target : null}
          disabled={this.disabled}
          class={this.getClasses()}
          onClick={e => this.onClickHandler(e)}>
          {this.icon && this.iconPosition !== 'right' ? (
            <snap-icon size={this.getIconSize()} color={this.variant} icon={this.icon}></snap-icon>
          ) : null}

          <span style={{'display' : this.iconOnlyMobile && this.isMobile ? 'none' : 'flex'}}>
            <slot />
          </span>

          {this.icon && this.iconPosition === 'right' ? (
            <snap-icon size={this.size === 'xs' || this.size === 'sm' ? 'xs' : 'sm'} color={this.variant} icon={this.icon}></snap-icon>
          ) : null}
        </ButtonTypeTag>
      </Host>
    )
  }
}
